import React, {
	useState,
	useMemo,
	useCallback,
	useEffect,
	useRef,
} from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';

import {
	setChonkyDefaults,
	ChonkyActions,
	FileHelper,
	FileBrowser,
	FileToolbar,
	defineFileAction,
	ChonkyIconName,
} from 'chonky';

import {
	ChonkyIconExtended,
} from './ChonkyIconExtended';

import {
	broncoURL,
	commitHash,
	vultureENV,
} from 'Vulture/ENV';

import {
	getSession,
} from 'Vulture/Session';

import {
	isSystemFolder,
	nestUploadsUnderAccountLibrary,
	transformFolderData,
	transformFileData,
	transformSearchFileData,
	transformSearchFolderData,
} from '../../../../Util/ChonkyFileBrowserETL';

import CustomIcon from '../../Common/CustomIcon/CustomIcon';
import CreateFolderModal from '../CreateFolderModal.js';
import CreateFileModal from '../CreateFileModal.js';
import EditFileModal from '../EditFileModal.js';
import EditFolderModal from '../EditFolderModal.js';
import ContentManagerDropdown from './ContentManagerDropdown.js';

import style from './ChonkyFileBrowser.module.css';
import CustomFileList from './ChonkyCustomFileList';
import CustomNavbar from './ChonkyCustomNavBar';

import {
	downloadFile,
} from '../utils/downloadFile';

import {
	downloadPercent,
	yieldForPaint,
} from 'Vulture/lib/backgroundDownloads.js';

import {
	getDownloadConfirmMessage,
	getThumbnailPath,
	isDocumentFile,
} from './lib';
import Img from 'wrapper/Img';
import { s3PublicURL } from 'Vulture/S3.js';

const chonkyOverrideStyle = document.createElement('style');
chonkyOverrideStyle.textContent = '.chonky-chonkyRoot { height: unset !important; } .chonky-searchFieldContainer {display: none !important; }';
document.head.appendChild(chonkyOverrideStyle);

setChonkyDefaults({
	iconComponent: ChonkyIconExtended,
});

const initialFileMap = {
	root: {
		id:          'root',
		name:        'Home',
		isDir:       true,
		childrenIds: [],
		parentId:    null,
	},
};

export const renderFolderIcon = (size = 28, solid = false) => {
	let innerContent = null;

	if (solid) {
		innerContent =
			<>
				<div className={`${style.folderIconContainer}`} style={{
					color: 'white',
					top:   '-12px',
					left:  '-1px',
				}}>
					<CustomIcon icon={[ 'fas', 'folder-open', ]} />
				</div>
				<div className={`${style.folderIconContainer}  ${style.folderIconShadow}`}>
					<CustomIcon icon={[ 'far', 'folder-open', ]} />
				</div>
			</>;
	} else {
		innerContent = <CustomIcon icon={[ 'far', 'folder-open', ]} />;
	}

	return (
		<span style={{
			fontSize:   size,
			lineHeight: 1,
			display:    'inline-flex',
			alignItems: 'center',
		}}>
			{innerContent}
		</span>
	);
};


function ChonkyFileBrowser(props) {
	const {
		app,
		accountUUID,
		isVCLEnabled,
		mode,
		onSelectImage,
		onSelectFolder,
		allowedFormats,
		readOnly = false,
		injectedFileMap,
		onInjectedFileOpen,
		injectedColumns,
		injectedHeaderDropdown,
		injectedHeaderDropdownInitialValue,
		injectedHeaderDropdownComponent,
		onInjectedEdit,
		onInjectedDelete,
		onInjectedCalendar,
		defaultFileViewActionId,
		onInjectedReorder,
		onInjectedReorderProgress,
		defaultSortColumn = 'name',
		defaultSortDirection = 'asc',
		injectedActions,
		hideActionsColumn = false,
		hideListViewOptions = false,
		hideEdit = false,
		hideEditAndDeleteButtons = false,
		onSelectionChange,
		onBreadcrumbClick,
		initialFolderId,
		addSelectAllToggleAction = true,
		searchAllColumns = false,
		onInjectedHeaderDropdownChange,
		injectedColumnDefaults = {},
	} = props;

	const isInjected = !!injectedFileMap;

	const pageSuffix = (isInjected && typeof mode === 'string') ? `_${mode}` : '';
	const storagePrefix = `chonky_${accountUUID || 'global'}${pageSuffix}`;

	const loadSessionState = (key, defaultVal) => {
		try {
			const raw = sessionStorage.getItem(`${storagePrefix}_${key}`);
			return raw !== null ? JSON.parse(raw) : defaultVal;
		} catch (e) { return defaultVal; }
	};

	const [ selectedFileIds, setSelectedFileIds, ] = useState(new Set());
	const [ fileMap, setFileMap, ] = useState(initialFileMap);
	const [ isListView, setIsListView, ] = useState(() => {
		const sessionVal = loadSessionState('isListView', null);
		if (typeof sessionVal === 'boolean') return sessionVal;
		return defaultFileViewActionId === ChonkyActions.EnableListView.id;
	});
	const [ currentFolderId, setCurrentFolderId, ] = useState(() => {
		const sessionVal = loadSessionState('currentFolderId', initialFolderId || 'root');
		return typeof sessionVal === 'string' ? sessionVal : (initialFolderId || 'root');
	});

	const [ storageScope, setStorageScope, ] = useState(storagePrefix);
	if (storageScope !== storagePrefix) {
		setStorageScope(storagePrefix);

		const savedFolderId = loadSessionState('currentFolderId', null);
		setCurrentFolderId(typeof savedFolderId === 'string' ? savedFolderId : (initialFolderId || 'root'));

		const savedListView = loadSessionState('isListView', null);
		setIsListView(typeof savedListView === 'boolean' ? savedListView : defaultFileViewActionId === ChonkyActions.EnableListView.id);

		if (!isInjected) setFileMap(initialFileMap);
	}

	useEffect(() => {
		try {
			sessionStorage.setItem(`${storagePrefix}_currentFolderId`, JSON.stringify(currentFolderId));
		} catch (e) {}
	}, [ currentFolderId, storagePrefix ]);

	useEffect(() => {
		try {
			sessionStorage.setItem(`${storagePrefix}_isListView`, JSON.stringify(isListView));
		} catch (e) {}
	}, [ isListView, storagePrefix ]);

	useEffect(() => {
		const hasLoadedFileMap = !!fileMap && (
			Object.keys(fileMap).length > 1 ||
			(Array.isArray(fileMap.root?.childrenIds) && fileMap.root.childrenIds.length > 0)
		);
		if (!hasLoadedFileMap) return;
		if (currentFolderId === 'root') return;
		if (!fileMap[currentFolderId]) {
			setCurrentFolderId(initialFolderId || 'root');
		}
	}, [ currentFolderId, fileMap, initialFolderId ]);

	const fileBrowserRef = useRef(null);
	const wrapperRef = useRef(null);
	const scrollContainerRef = useRef(null);
	const fileMapRef = useRef(fileMap);
	const fileIconErrorMap = useRef({});
	const accountUUIDRef = useRef(accountUUID);
	accountUUIDRef.current = accountUUID;

	const draggedFileRef = useRef(null);
	const handledInternalDropRef = useRef(false);
	const [ draggingFileId, setDraggingFileId, ] = useState(null);

	const [ isWrapperDragging, setIsWrapperDragging ] = useState(false);
	const wrapperDragCounter = useRef(0);

	const [ breadcrumbDropTargetId, setBreadcrumbDropTargetId, ] = useState(null);
	const [ dragOverIndex, setDragOverIndex, ] = useState(null);
	const [ dropTarget, setDropTarget, ] = useState(null);

	const isDraggingRef = useRef(false);
	const dragPosRef = useRef({
		x: 0,
		y: 0,
	});
	const rafRef = useRef(null);
	const SCROLL_EDGE_PX = 80; // distance from edge to start scrolling
	const SCROLL_MAX_SPEED = 25; // px per frame

	const getActiveScrollContainer = useCallback(() => {
		const candidates = [ scrollContainerRef.current, wrapperRef.current, ];
		for (const el of candidates) {
			if (el && el.scrollHeight > el.clientHeight + 1) return el;
		}
		return null;
	}, []);

	const autoScrollStep = useCallback(() => {
		if (!isDraggingRef.current) {
			rafRef.current = null;
			return;
		}

		const container = getActiveScrollContainer();
		const y = dragPosRef.current.y;

		if (container) {
			const rect = container.getBoundingClientRect();
			const fromTop = y - rect.top;
			const fromBottom = rect.bottom - y;
			let deltaY = 0;
			if (fromTop < SCROLL_EDGE_PX && fromTop >= -10) {
				deltaY = -((SCROLL_EDGE_PX - fromTop) / SCROLL_EDGE_PX) * SCROLL_MAX_SPEED;
			} else if (fromBottom < SCROLL_EDGE_PX && fromBottom >= -10) {
				deltaY = ((SCROLL_EDGE_PX - fromBottom) / SCROLL_EDGE_PX) * SCROLL_MAX_SPEED;
			}
			if (deltaY !== 0) {
				container.scrollTop += deltaY;
			}
		} else {
			// Window fallback
			const vh = window.innerHeight;
			const fromTop = y;
			const fromBottom = vh - y;
			let deltaY = 0;
			if (fromTop < SCROLL_EDGE_PX && fromTop >= -10) {
				deltaY = -((SCROLL_EDGE_PX - fromTop) / SCROLL_EDGE_PX) * SCROLL_MAX_SPEED;
			} else if (fromBottom < SCROLL_EDGE_PX && fromBottom >= -10) {
				deltaY = ((SCROLL_EDGE_PX - fromBottom) / SCROLL_EDGE_PX) * SCROLL_MAX_SPEED;
			}
			if (deltaY !== 0) {
				window.scrollBy(0, deltaY);
				// scrolled = true;
			}
		}

		// Keep the loop running while dragging
		rafRef.current = requestAnimationFrame(autoScrollStep);
	}, [ getActiveScrollContainer, ]);

	const startAutoScroll = useCallback(() => {
		if (isDraggingRef.current) return;
		isDraggingRef.current = true;
		if (!rafRef.current) rafRef.current = requestAnimationFrame(autoScrollStep);
	}, [ autoScrollStep, ]);

	const stopAutoScroll = useCallback(() => {
		isDraggingRef.current = false;
		if (rafRef.current) {
			cancelAnimationFrame(rafRef.current);
			rafRef.current = null;
		}
	}, []);

	const handleWrapperDragOver = useCallback((e) => {
		// Track cursor for autoscroll. Avoid preventDefault here to not interfere with item-level DnD.
		dragPosRef.current = {
			x: e.clientX,
			y: e.clientY,
		};
		if (!isDraggingRef.current) startAutoScroll();

		if (!draggedFileRef.current && e.dataTransfer.types && e.dataTransfer.types.includes('Files')) {
			e.preventDefault();
			e.dataTransfer.dropEffect = 'copy';
		}
	}, [ startAutoScroll, ]);

	const [ searchQuery, setSearchQuery, ] = useState('');
	const [ searchResults, setSearchResults, ] = useState([]);

	const [ sortColumn, setSortColumn, ] = useState(defaultSortColumn);
	const [ sortDirection, setSortDirection, ] = useState(defaultSortDirection);

	const hasSearchTerm = isInjected ? false : (typeof searchQuery === 'string' && searchQuery.trim().length > 2);

	const folderChain = useMemo(() => {
		const chain = [];
		let currentId = currentFolderId;

		while (currentId) {
			const folder = fileMap[currentId];
			if (!folder) {
				if (currentId !== 'root' && fileMap['root']) {
					chain.unshift({ id: currentId, name: '...', isDir: true, parentId: 'root' });
					chain.unshift(fileMap['root']);
				}
				break;
			}
			chain.unshift(folder);
			currentId = folder.parentId;
		}

		return chain;
	}, [ currentFolderId, fileMap, ]);

	const stockLibraryWriteAccess = props.permissions?.permissions?.includes('library_edit');
	const accountLibraryWriteAccess = props.permissions?.permissions?.includes('gallery_upload');
	const accountLibraryDeleteAccess = props.permissions?.permissions?.includes('gallery_delete');

	const isRoot = currentFolderId === 'root';
	const isStock = folderChain.some((folder) => {
		return folder.parentId === 'root' && folder.name === 'Stock Library';
	});

	const hasWriteAccess = !isRoot && ((isStock && stockLibraryWriteAccess) || (!isStock && accountLibraryWriteAccess));
	const hasDeleteAccess = !isRoot && ((isStock && stockLibraryWriteAccess) || (!isStock && accountLibraryDeleteAccess));

	const getDisplayPath = useCallback((item) => {
		if (!item || item.isDir) return '-';
		const names = [];
		let parentId = item.parentId;
		while (parentId && parentId !== 'root') {
			const node = fileMap[parentId];
			if (!node) break;
			names.unshift(node.name || '');
			parentId = node.parentId;
		}
		return names.join('/');
	}, [ fileMap, ]);

	const files = useMemo(() => {
		if (hasSearchTerm) {
			let folderResults = [];
			let fileResults = [];
			let completeResults = [];

			if (searchResults?.folders) {
				folderResults = transformSearchFolderData(searchResults?.folders);
			}
			if (searchResults?.files) {
				fileResults = transformSearchFileData(searchResults?.files);
			}

			completeResults = [ ...folderResults, ...fileResults, ];

			return completeResults;
		}

		const currentFolder = fileMap[currentFolderId];
		if (!currentFolder) return [];

		return currentFolder.childrenIds.map((id) => {
			return fileMap[id];
		}).filter(Boolean);

	}, [ currentFolderId, fileMap, hasSearchTerm, searchResults, ]);

	const resolvedInjectedColumns = useMemo(() => {
		if (!isInjected) return [];
		if (Array.isArray(injectedColumns) && injectedColumns.length > 0) return injectedColumns;
		return [
			{
				id:       'name',
				label:    'Name',
				accessor: (f) => {
					return f.name;
				},
			},
		];
	}, [ isInjected, injectedColumns, ]);

	const injectedFilteredFiles = useMemo(() => {
		if (!isInjected) return files;
		const q = searchQuery.trim().toLowerCase();
		if (!q) return files;
		if (!searchAllColumns) {
			return files.filter((f) => {
				return (f?.name || '').toLowerCase().includes(q);
			});
		}
		return files.filter((f) => {
			const matchesColumn = resolvedInjectedColumns.some((col) => {
				let val;
				if (typeof col.accessor === 'function') {
					try {
						val = col.accessor(f);
					} catch (_) { /* ignore */ }
				} else if (col.id) {
					val = f[col.id];
				}
				if (val === undefined || val === null) return false;
				return String(val).toLowerCase().includes(q);
			});

			if (matchesColumn) return true;

			if (f && f.assignmentData && typeof f.assignmentData === 'object') {
				const collectPrimitives = (obj, acc) => {
					if (!obj) return;
					if (typeof obj !== 'object') {
						acc.push(String(obj));
						return;
					}
					if (Array.isArray(obj)) {
						obj.forEach((v) => {
							collectPrimitives(v, acc);
						});
						return;
					}
					for (const k in obj) {
						if (Object.prototype.hasOwnProperty.call(obj, k)) {
							collectPrimitives(obj[k], acc);
						}
					}
				};
				const vals = [];
				collectPrimitives(f.assignmentData, vals);
				const haystack = vals.join(' ').toLowerCase();
				if (haystack.includes(q)) return true;
			}

			return false;
		});
	}, [ files, isInjected, searchQuery, searchAllColumns, resolvedInjectedColumns, ]);

	const effectiveFiles = isInjected ? injectedFilteredFiles : files;

	const filesSortedByNames = useMemo(() => {
		// helper: prefer injected column accessor value when available.
		// Callers whose accessor returns JSX can attach a `value` prop on the
		// outer React element to control the sort key without changing the
		// visible cell content — e.g. parent/child row groupings that need to
		// stay adjacent under alphabetical sort
		// (see VisualContentLoopAssignments.js).
		const resolveSortValue = (item, col) => {
			if (isInjected && Array.isArray(resolvedInjectedColumns)) {
				const colDef = resolvedInjectedColumns.find((c) => {
					return String(c.id) === String(col);
				});
				if (colDef) {
					if (typeof colDef.accessor === 'function') {
						try {
							const val = colDef.accessor(item);
							if (React && React.isValidElement && React.isValidElement(val)) {
								if (val.props && val.props.value !== undefined) return val.props.value;
								if (typeof val.props.children === 'string') return val.props.children;
								return '';
							}
							return val !== undefined && val !== null ? val : '';
						} catch (e) {
							console.error('Error in injected column accessor:', e);
						}
					}
					if (colDef.id && item && item[colDef.id] !== undefined) return item[colDef.id];
				}
			}
			return item[col] !== undefined && item[col] !== null ? item[col] : '';
		};

		const sorted = [ ...effectiveFiles ];
		sorted.sort((a, b) => {
			let aVal = resolveSortValue(a, sortColumn);
			let bVal = resolveSortValue(b, sortColumn);
			if (sortColumn === 'size') {
				aVal = a.size || 0;
				bVal = b.size || 0;
			} else if (sortColumn === 'resolution') {
				aVal = (a.resolution || '').toLowerCase();
				bVal = (b.resolution || '').toLowerCase();
			} else if (sortColumn === 'description') {
				aVal = (a.description || '').toLowerCase();
				bVal = (b.description || '').toLowerCase();
			} else if (sortColumn === 'order') {
				aVal = a.order || 0;
				bVal = b.order || 0;
			} else if (sortColumn === 'slots') {
				aVal = a.folderSlots || 0;
				bVal = b.folderSlots || 0;
			} else {
				// Default case for 'name' and other string fields - case insensitive
				aVal = (aVal || '').toString().toLowerCase();
				bVal = (bVal || '').toString().toLowerCase();
			}
			if (sortColumn === 'path') {
				aVal = getDisplayPath(a) || '';
				bVal = getDisplayPath(b) || '';
			}
			if (a.isDir !== b.isDir) return a.isDir ? -1 : 1; // Always sort folders first
			if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
			if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
			return 0;
		});
		return sorted;
	}, [ effectiveFiles, getDisplayPath, sortColumn, sortDirection, isInjected, resolvedInjectedColumns ]);

	const handleSort = (column) => {
		if (sortColumn === column) {
			setSortDirection((dir) => {
				return dir === 'asc' ? 'desc' : 'asc';
			});
		} else {
			setSortColumn(column);
			setSortDirection('asc');
		}
	};

	const renderSortArrow = (column) => {
		if (sortColumn !== column) return <span style={{
			marginLeft: 4,
		}} data-test-id={`sortArrow-${column}-none`}>⇅</span>;
		return (
			<span style={{
				marginLeft: 4,
			}} data-test-id={`sortArrow-${column}-${sortDirection}`}>
				{sortDirection === 'asc' ? '▲' : '▼'}
			</span>
		);
	};

	useEffect(() => {
		fileMapRef.current = fileMap;
	}, [ fileMap, ]);

	const prevFileViewActionId = useRef(defaultFileViewActionId);
	useEffect(() => {
		if (prevFileViewActionId.current === defaultFileViewActionId) return;
		prevFileViewActionId.current = defaultFileViewActionId;

		if (defaultFileViewActionId === ChonkyActions.EnableListView.id) {
			setIsListView(true);
		} else if (defaultFileViewActionId === ChonkyActions.EnableGridView.id) {
			setIsListView(false);
		}
	}, [ defaultFileViewActionId, ]);

	useEffect(() => {
		if (!isInjected) return;
		if (injectedFileMap && typeof injectedFileMap === 'object') {
			setFileMap(injectedFileMap);
			const candidateRoot = initialFolderId || (injectedFileMap.root ? 'root' : Object.keys(injectedFileMap)[0]);
			if (candidateRoot) setCurrentFolderId(candidateRoot);
		}
	}, [ injectedFileMap, isInjected, initialFolderId, ]);

	useEffect(() => {
		if (fileBrowserRef.current) {
			setSelectedFileIds(new Set(fileBrowserRef.current.getFileSelection()));
		}
	}, [ filesSortedByNames, ]);

	// Push internal selection changes up to parent (with loop guard)
	const lastSentSelectionRef = useRef('');
	useEffect(() => {
		if (typeof onSelectionChange !== 'function') return;
		const arr = Array.from(selectedFileIds);
		const key = arr.slice().sort().join('|');
		if (key === lastSentSelectionRef.current) return; // no change
		lastSentSelectionRef.current = key;
		onSelectionChange(arr);
	}, [ selectedFileIds, onSelectionChange, ]);

	// Notify parent when current folder changes
	const {
		onCurrentFolderChange,
	} = props;
	const lastSentFolderRef = useRef('');
	useEffect(() => {
		if (typeof onCurrentFolderChange !== 'function') return;
		if (currentFolderId === lastSentFolderRef.current) return; // no change
		lastSentFolderRef.current = currentFolderId;
		onCurrentFolderChange(currentFolderId, fileMap);
	}, [ currentFolderId, fileMap, onCurrentFolderChange, ]);

	// silent: skip the app-level loading overlay — for background syncs (e.g.
	// the Edit modal refreshing after a generation finishes) where a spinner
	// flash behind an open modal is jarring.
	const refreshFileBrowserData = useCallback(async ({ silent = false } = {}) => {
		if (isInjected) return; // Skip backend fetch when injected
		if (!silent) {
			app.setState({
				loading: true,
			});
		}

		try {
			const session = getSession();
			const folderUrl = `${broncoURL}/api/folder?account_uuid=${accountUUID}`;
			const headers = {
				headers: {
					'X-Auth-Session':      session.session,
					'X-Spark-API-version': commitHash,
				},
			};

			const folderResponse = await axios.get(folderUrl, headers);
			if (accountUUIDRef.current !== accountUUID) return;
			if (folderResponse.status !== 200) {
				throw new Error(`Invalid response from ${folderUrl}`);
			}

			const newFileMap = transformFolderData(nestUploadsUnderAccountLibrary(folderResponse.data));

			if (!isRoot && !newFileMap[currentFolderId]) {
				setFileMap(newFileMap);
				setCurrentFolderId(initialFolderId || 'root');
				return newFileMap;
			}

			if (!isRoot) {
				const backendFolderId = currentFolderId.replace('node_', '');
				const filesUrl = `${broncoURL}/api/folder/${backendFolderId}/file?account_uuid=${accountUUID}`;
				const filesResponse = await axios.get(filesUrl, headers);
				if (accountUUIDRef.current !== accountUUID) return;

				if (filesResponse.status === 200) {
					const mapWithFiles = transformFileData(newFileMap, currentFolderId, filesResponse.data.files);
					setFileMap(mapWithFiles);
					return mapWithFiles;
				}
			}

			setFileMap(newFileMap);
			return newFileMap;
		} catch (err) {
			console.error('Refresh error:', err);
		} finally {
			if (!silent) {
				app.setState({
					loading: false,
				});
			}
		}
	}, [ isInjected, app, accountUUID, isRoot, currentFolderId, initialFolderId, ]);

	const openCreateFolderModal = useCallback(() => {
		app.openModal({
			modalSize:  1,
			showModal:  true,
			modalProps: {
				title: 'Add New Folder',
				jsx:   (
					<CreateFolderModal
						currentFolderId={currentFolderId}
						refreshFileBrowserData={refreshFileBrowserData}
						app={app}
						accountUUID={accountUUID}
					/>
				),
			},
		});
	}, [ accountUUID, app, currentFolderId, refreshFileBrowserData, ]);

	const openCreateFileModal = useCallback(() => {
		app.openModal({
			modalSize:  1,
			showModal:  true,
			modalProps: {
				title: 'Upload New File',
				jsx:   (
					<CreateFileModal
						currentFolderId={currentFolderId}
						refreshFileBrowserData={refreshFileBrowserData}
						isVCLEnabled={isVCLEnabled}
						app={app}
						accountUUID={accountUUID}
					/>
				),
			},
		});
	}, [ accountUUID, app, currentFolderId, isVCLEnabled, refreshFileBrowserData, ]);

	const fetchDownloadStats = useCallback(async (filesToProcess) => {
		const fileIds = filesToProcess.filter((f) => {
			return !f.isDir;
		}).map((f) => {
			return f.id.startsWith('file_') ? f.id.slice(5) : f.id;
		});
		const folderIds = filesToProcess.filter((f) => {
			return f.isDir;
		}).map((f) => {
			return f.id.startsWith('node_') ? f.id.slice(5) : f.id;
		});

		const localStats = {
			numFiles:      fileIds.length,
			numFolders:    folderIds.length,
			numSubFolders: 0,
			totalSize:     filesToProcess.reduce((acc, f) => {
				return acc + (f.size || 0);
			}, 0),
		};

		if (folderIds.length === 0) {
			return localStats;
		}

		const statsID = app.startBackgroundDownload('Calculating download size...');

		try {
			const session = getSession();
			const response = await axios.post(`${broncoURL}/api/gallery/download/stats?account_uuid=${accountUUID}`, {
				file_ids:   fileIds,
				folder_ids: folderIds,
			}, {
				headers: {
					'X-Auth-Session':      session.session,
					'X-Spark-API-version': commitHash,
				},
			});

			return {
				numFiles:      response.data.num_files,
				numFolders:    response.data.num_folders ?? localStats.numFolders,
				numSubFolders: response.data.num_subfolders || 0,
				totalSize:     response.data.total_size,
			};
		} catch (error) {
			console.error('Failed to fetch download stats', error);
			return localStats;
		} finally {
			app.endBackgroundDownload(statsID);
			await yieldForPaint();
		}
	}, [ accountUUID, app, ]);

	const handleDownloadFile = async (file, specificFilesToDownload = null, knownStats = null) => {
		let filesToProcess = [];
		if (specificFilesToDownload && specificFilesToDownload.length > 0) {
			filesToProcess = specificFilesToDownload;
		} else if (file) {
			filesToProcess = [file];
		} else {
			const currentSelection = Array.from(fileBrowserRef.current.getFileSelection());
			filesToProcess = currentSelection.map((id) => fileMapRef.current[id]).filter(Boolean);
		}

		if (filesToProcess.length === 0) {
			alert('Please select at least one file or folder to download');
			return;
		}

		const hasFolders = filesToProcess.some((f) => {
			return f.isDir;
		});

		if (!specificFilesToDownload && (filesToProcess.length > 1 || hasFolders)) {
			const stats = await fetchDownloadStats(filesToProcess);

			if (!window.confirm(getDownloadConfirmMessage(stats))) {
				return;
			}

			return handleDownloadFile(null, filesToProcess, stats);
		}

		if (hasFolders || filesToProcess.length > 1) {
			return handleDownloadZip(filesToProcess, knownStats);
		}

		for (let i = 0; i < filesToProcess.length; i++) {
			const fileToDownload = filesToProcess[i];
			const fileUrl = s3PublicURL('ts-condor-assets', `${vultureENV}/ts-library/${fileToDownload.path}`);
			const fileName = fileToDownload.name || 'downloaded_file';

			const downloadID = app.startBackgroundDownload(`Downloading ${fileName}`);

			try {
				await downloadFile(fileUrl, fileName, (percent) => {
					app.updateBackgroundDownload(downloadID, {
						percent: Math.min(99, percent),
					});
				});

				if (i < filesToProcess.length - 1) {
					await new Promise((resolve) => {
						setTimeout(resolve, 500);
					});
				}
			} catch (error) {
				console.error('Download failed:', error);
				window.open(fileUrl, '_blank');
			} finally {
				app.endBackgroundDownload(downloadID);
			}
		}
	};

	const performSearch = useCallback(async () => {
		if (!hasSearchTerm) {
			return;
		}

		try {
			app.setState({
				loading: true,
			});

			const session = getSession();
			let url = `${broncoURL}/api/search?query=${searchQuery}&account_uuid=${accountUUID}`;

			if (!isRoot) {
				url = `${broncoURL}/api/search/${currentFolderId.slice(5)}?query=${searchQuery}&account_uuid=${accountUUID}`;
			}

			const headers = {
				headers: {
					'X-Auth-Session':      session.session,
					'X-Spark-API-version': commitHash,
				},
			};

			const res = await axios.get(url, headers);

			if (res.status !== 200) {
				throw new Error(`Invalid response from ${broncoURL}/api/search`);
			}

			setSearchResults(res.data);
		} catch (err) {
			// eslint-disable-next-line no-console
			// console.error('Search failed:', err);
		} finally {
			app.setState({
				loading: false,
			});
		}
	}, [ hasSearchTerm, app, searchQuery, accountUUID, isRoot, currentFolderId, ]);

	const refreshLibraryView = useCallback(() => {
		if (searchQuery) {
			performSearch();
		}
		if (currentFolderId === 'root') return;

		const folder = fileMapRef.current[currentFolderId];
		if (!folder) return;

		const hasFiles = folder.childrenIds.some((id) => {
			return id.startsWith('file_');
		});

		if (hasFiles) return;

		app.setState({
			loading: true,
		});

		const backendFolderId = currentFolderId.replace('node_', '');
		const session = getSession();
		const url = `${broncoURL}/api/folder/${backendFolderId}/file?account_uuid=${accountUUID}`;
		const headers = {
			headers: {
				'X-Auth-Session':      session.session,
				'X-Spark-API-version': commitHash,
			},
		};

		axios.get(url, headers)
			.then((res) => {
				app.setState({
					loading: false,
				});
				if (res.status !== 200) throw new Error(`Invalid response from ${url}`);
				return res.data.files;
			})
			.then((filesArray) => {
				setFileMap((prevMap) => {
					return transformFileData(prevMap, currentFolderId, filesArray);
				});
			})
			.catch((err) => {
				// eslint-disable-next-line no-console
				console.error('File fetch error:', err);
				// eslint-disable-next-line no-alert
				alert(`Failed to load files: ${err.response?.data?.message || err.message}`);
				app.setState({
					loading: false,
				});
			});
	}, [ searchQuery, currentFolderId, app, accountUUID, performSearch, ]);

	const openEditModal = useCallback((item) => {
		if (!item || isSystemFolder(item)) return;
		if (item.id.startsWith('file_')) {
			app.openModal({
				modalSize:  1,
				showModal:  true,
				modalProps: {
					title: 'Edit File',
					jsx:   (
						<EditFileModal
							file={item}
							refreshFileBrowserData={refreshFileBrowserData}
							app={app}
							accountUUID={accountUUID}
							isVCLEnabled={isVCLEnabled}
							refreshLibraryView={refreshLibraryView}
						/>
					),
				},
			});
		} else if (item.id.startsWith('node_')) {
			app.openModal({
				modalSize:  1,
				showModal:  true,
				modalProps: {
					title: 'Edit Folder',
					jsx:   (
						<EditFolderModal
							folder={item}
							refreshFileBrowserData={refreshFileBrowserData}
							app={app}
							accountUUID={accountUUID}
						/>
					),
				},
			});
		}
	}, [ accountUUID, app, refreshFileBrowserData, refreshLibraryView, ]);

	useEffect(() => {
		if (isInjected) return;
		refreshFileBrowserData();
	}, [ refreshFileBrowserData, isInjected, ]);

	useEffect(() => {
		const timer = setTimeout(() => {
			performSearch();
		}, 300);

		return () => {
			clearTimeout(timer);
		};
	}, [ searchQuery, performSearch, ]);

	const renderFileIcon = (file, size = 28) => {
		const imgError = fileIconErrorMap.current[file.id] || false;
		if (file.isDir) return renderFolderIcon(size);
		// Documents have no generated thumbnail — show the default PDF icon
		// directly (matches the uploader) instead of 404ing into the generic
		// file fallback.
		if (isDocumentFile(file.name)) return (
			<span style={{
				fontSize:   size,
				lineHeight: 1,
				display:    'inline-flex',
				alignItems: 'center',
			}}>
				<CustomIcon icon='file-pdf' color='#00baff' />
			</span>
		);
		if (imgError) return (
			<span style={{
				fontSize:   size,
				lineHeight: 1,
				display:    'inline-flex',
				alignItems: 'center',
			}}>
				<CustomIcon icon={[ 'far', 'file', ]} />
			</span>
		);

		return (
			<Img
				draggable={false}
				src={getThumbnailPath(file.path)}
				alt="file"
				style={{
					width:         size,
					height:        size,
					objectFit:     'contain',
					verticalAlign: 'middle',
				}}
				onError={() => {
					fileIconErrorMap.current[file.id] = true;
					setFileMap((fm) => {
						return {
							...fm,
						};
					});
				}}
			/>
		);
	};

	const handleCheckboxChange = (file, checked) => {
		const selection = new Set(selectedFileIds);
		if (checked) {
			selection.add(file.id);
		} else {
			selection.delete(file.id);
		}
		fileBrowserRef.current.setFileSelection(selection);
		setSelectedFileIds(new Set(selection));
	};

	const handleDragStart = (e, file) => {
		draggedFileRef.current = file;
		e.dataTransfer.effectAllowed = 'move';
		e.dataTransfer.setData('application/x-ts-file-id', file.id);

		e.dataTransfer.setData('text/plain', '');

		e.target.style.opacity = '0.5';
		setTimeout(() => {
			if (draggedFileRef.current && draggedFileRef.current.id === file.id) {
				setDraggingFileId(file.id);
			}
		}, 0);
		startAutoScroll();
	};

	const confirmAndDownloadDraggedFiles = useCallback(async (filesToDownload) => {
		const stats = await fetchDownloadStats(filesToDownload);

		if (!window.confirm(getDownloadConfirmMessage(stats))) {
			return;
		}

		handleDownloadFile(null, filesToDownload, stats);
	}, [ fetchDownloadStats, handleDownloadFile, ]);

	const handleDragEnd = useCallback((e, file) => {
		e.target.style.opacity = '1';

		if (!handledInternalDropRef.current && !isInjected) {
			let filesToDownload = selectedFileIds.size > 0 ?
				Array.from(selectedFileIds).map((id) => {
					return fileMapRef.current[id];
				}).filter(Boolean) :
				[ draggedFileRef.current, ].filter(Boolean);

			if (!selectedFileIds.has(draggedFileRef.current?.id)) {
				filesToDownload = [ draggedFileRef.current, ].filter(Boolean);
			}

			if (filesToDownload.length > 0) {
				// Intentionally not awaited: the drag state below has to be reset in this
				// same tick, and the download itself runs in the background.
				confirmAndDownloadDraggedFiles(filesToDownload);
			}
		}

		draggedFileRef.current = null;
		setDraggingFileId(null);
		setDropTarget(null);
		setDragOverIndex(null);
		handledInternalDropRef.current = false;
		stopAutoScroll();
	}, [ selectedFileIds, isInjected, stopAutoScroll, confirmAndDownloadDraggedFiles, ]);

	const handleDownloadZip = useCallback(async (filesToDownload, knownStats = null) => {
		const expectedBytes = knownStats?.totalSize || 0;
		const downloadID = app.startBackgroundDownload('Preparing download...');
		let failed = false;

		try {
			const session = getSession();
			const fileIds = filesToDownload.filter((f) => {
				return !f.isDir;
			}).map((f) => {
				return f.id.startsWith('file_') ? f.id.slice(5) : f.id;
			});
			const folderIds = filesToDownload.filter((f) => {
				return f.isDir;
			}).map((f) => {
				return f.id.startsWith('node_') ? f.id.slice(5) : f.id;
			});

			const response = await axios.post(`${broncoURL}/api/gallery/download?account_uuid=${accountUUID}`, {
				file_ids:   fileIds,
				folder_ids: folderIds,
			}, {
				headers: {
					'X-Auth-Session':      session.session,
					'X-Spark-API-version': commitHash,
				},
				responseType:       'blob',
				onDownloadProgress: (progressEvent) => {
					const receivedBytes = progressEvent.loaded || 0;
					const totalBytes = progressEvent.total || expectedBytes;

					app.updateBackgroundDownload(downloadID, {
						label:   'Downloading...',
						receivedBytes,
						totalBytes,
						percent: downloadPercent(receivedBytes, totalBytes),
					});
				},
			});

			const url = window.URL.createObjectURL(new Blob([ response.data, ]));
			try {
				const link = document.createElement('a');
				link.href = url;
				link.setAttribute('download', 'gallery_download.zip');
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			} finally {
				window.URL.revokeObjectURL(url);
			}
		} catch (error) {
			console.error('Download error:', error);
			failed = true;
		} finally {
			app.endBackgroundDownload(downloadID);
		}

		if (failed) {
			await yieldForPaint();
			alert('Failed to download files.');
		}
	}, [ accountUUID, app, ]);

	const handleDragOver = (e, file, dropIndex) => {
		if (!draggedFileRef.current) return;

		// Handle reordering for injected mode
		if (isInjected && onInjectedReorderProgress) {
			e.preventDefault();
			e.dataTransfer.dropEffect = 'move';

			const draggedFile = draggedFileRef.current;
			const currentIndex = files.findIndex((f) => {
				return f.id === draggedFile.id;
			});

			if (currentIndex === dropIndex) {
				return;
			}

			setDragOverIndex(dropIndex);
			onInjectedReorderProgress(draggedFile, currentIndex, dropIndex);
			return;
		}

		// Original folder drop logic
		if (!file.isDir) return;

		e.preventDefault();
		e.dataTransfer.dropEffect = 'move';
		setDropTarget(file.id);
	};

	const handleDragLeave = (e, file) => {
		if (e.currentTarget.contains(e.relatedTarget)) return;
		setDropTarget(null);
		setDragOverIndex(null);
	};

	const handleDrop = (e, destinationFile, dropIndex) => {
		e.preventDefault();
		setDropTarget(null);
		setDragOverIndex(null);
		setDraggingFileId(null);

		if (!draggedFileRef.current) return;

		// Handle reordering for injected mode
		if (isInjected && onInjectedReorder && typeof dropIndex === 'number') {
			const draggedFile = draggedFileRef.current;
			const currentIndex = files.findIndex((f) => {
				return f.id === draggedFile.id;
			});

			if (currentIndex !== -1) {
				onInjectedReorder(draggedFile, currentIndex, dropIndex);
			}

			draggedFileRef.current = null;
			stopAutoScroll();
			return;
		}

		// Original folder drop logic
		if (!destinationFile.isDir) return;

		const filesToMove = getFilesToMove(draggedFileRef.current);

		handleFileAction({
			id:      ChonkyActions.EndDragNDrop.id,
			payload: {
				draggedFile:   draggedFileRef.current,
				destination:   destinationFile,
				selectedFiles: filesToMove,
			},
		});

		draggedFileRef.current = null;
		stopAutoScroll();
	};

	const handleWrapperDrop = useCallback(async (e) => {
		stopAutoScroll();
		wrapperDragCounter.current = 0;
		setIsWrapperDragging(false);

		if (draggedFileRef.current) return;

		e.preventDefault();
		e.stopPropagation();

		if (!e.dataTransfer.items || e.dataTransfer.items.length === 0) return;

		const fileEntries = [];
		let hasSubdirectories = false;
		const rootFolderNames = new Set();

		const readDirectory = (directoryReader) => {
			return new Promise((resolve, reject) => {
				const allEntries = [];
				const readBatch = () => {
					directoryReader.readEntries((batch) => {
						if (!batch || batch.length === 0) {
							return resolve(allEntries);
						}
						allEntries.push(...batch);
						return readBatch();
					}, reject);
				};
				readBatch();
			});
		};

		const traverseEntry = async (entry, isRoot = true, currentFolderName = null) => {
			if (entry.isFile) {
				const file = await new Promise((resolve) => {
					return entry.file(resolve);
				});
				file.targetFolder = currentFolderName;
				fileEntries.push(file);
			} else if (entry.isDirectory) {
				if (isRoot) {
					rootFolderNames.add(entry.name);
					currentFolderName = entry.name;
				} else {
					hasSubdirectories = true;
					return;
				}
				const dirReader = entry.createReader();
				const entries = await readDirectory(dirReader);
				for (let i = 0; i < entries.length; i++) {
					await traverseEntry(entries[i], false, currentFolderName);
					if (hasSubdirectories) break;
				}
			}
		};

		const entries = [];
		for (let i = 0; i < e.dataTransfer.items.length; i++) {
			const item = e.dataTransfer.items[i];
			if (item.kind === 'file') {
				if (typeof item.webkitGetAsEntry === 'function') {
					const entry = item.webkitGetAsEntry();
					if (entry) {
						entries.push(entry);
					}
				} else {
					const file = typeof item.getAsFile === 'function' ? item.getAsFile() : null;
					if (file) {
						fileEntries.push(file);
					}
				}
			}
		}

		for (const entry of entries) {
			await traverseEntry(entry, true, null);
		}

		if (hasSubdirectories) {
			app.openModal({
				modalSize:  1,
				showModal:  true,
				modalProps: {
					title: 'Upload Canceled',
					jsx:   (
						<div style={{
							padding: '20px',
						}}>
							<h3 style={{
								color:     'var(--ts-red, red)',
								marginTop: 0,
							}}>Upload canceled</h3>
							<p>Folder uploads cannot include subfolders.</p>
							<div style={{
								marginTop: '20px',
								textAlign: 'right',
							}}>
								<button className="button" onClick={() => {
									return app.closeModal();
								}}>OK</button>
							</div>
						</div>
					),
				},
			});
			return;
		}

		if (fileEntries.length > 20) {
			app.openModal({
				modalSize:  1,
				showModal:  true,
				modalProps: {
					title: 'Upload Canceled',
					jsx:   (
						<div style={{
							padding: '20px',
						}}>
							<h3 style={{
								color:     'var(--ts-red, red)',
								marginTop: 0,
							}}>Upload canceled</h3>
							<p>Maximum 20 files allowed at a time.</p>
							<div style={{
								marginTop: '20px',
								textAlign: 'right',
							}}>
								<button className="button" onClick={() => {
									return app.closeModal();
								}}>OK</button>
							</div>
						</div>
					),
				},
			});
			return;
		}

		if (fileEntries.length === 0) return;

		app.openModal({
			modalSize:  1,
			showModal:  true,
			modalProps: {
				title: 'Upload New File',
				jsx:   (
					<CreateFileModal
						currentFolderId={currentFolderId}
						refreshFileBrowserData={refreshFileBrowserData}
						isVCLEnabled={isVCLEnabled}
						app={app}
						accountUUID={accountUUID}
						initialFiles={fileEntries}
						folderNames={Array.from(rootFolderNames)}
					/>
				),
			},
		});

	}, [ stopAutoScroll, app, currentFolderId, refreshFileBrowserData, isVCLEnabled, accountUUID, ]);

	const handleWrapperDragEnter = useCallback((e) => {
		e.preventDefault();
		if (draggedFileRef.current) return;
		wrapperDragCounter.current += 1;
		if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
			setIsWrapperDragging(true);
		}
	}, []);

	const handleWrapperDragLeave = useCallback((e) => {
		e.preventDefault();
		if (draggedFileRef.current) return;
		wrapperDragCounter.current -= 1;
		if (wrapperDragCounter.current <= 0) {
			wrapperDragCounter.current = 0;
			setIsWrapperDragging(false);
		}
	}, []);

	const handleDelete = (e, file) => {
		e.stopPropagation();
		const newSelectedFileIds = new Set([ file.id, ]);
		setSelectedFileIds(newSelectedFileIds);
		fileBrowserRef.current.setFileSelection(newSelectedFileIds);
		handleFileAction({
			id:      ChonkyActions.DeleteFiles.id,
			payload: {
				selectedFileIds: newSelectedFileIds,
			},
		});
	};

	const getFilesToMove = useCallback((draggedFile) => {
		const selectionIds = Array.from(selectedFileIds || []);
		const draggingIsSelected = (selectedFileIds && selectedFileIds.has(draggedFile?.id));
		let filesToMove = [ draggedFile, ];
		if (draggingIsSelected && selectionIds.length > 1) {
			filesToMove = selectionIds
				.map((id) => {
					return fileMapRef.current[id] || filesSortedByNames.find((f) => {
						return f.id === id;
					});
				})
				.filter((f) => {
					return !!f;
				});
			if (filesToMove.length === 0 && draggedFile) filesToMove = [ draggedFile, ];
		}
		return filesToMove;
	}, [ filesSortedByNames, selectedFileIds, ]);

	const handleFileAction = useCallback((data) => {
		if (isInjected && Array.isArray(injectedActions) && injectedActions.length > 0) {
			const matched = injectedActions.find((a) => {
				return a.id === data.id;
			});
			if (matched) {
				try {
					if (typeof matched.handler === 'function') {
						const selection = Array.from(fileBrowserRef.current?.getFileSelection?.() || []);
						matched.handler({
							selection,
							currentFolderId,
							files:        filesSortedByNames,
							app,
							open:         onInjectedFileOpen,
							data,
							setSelection: (ids) => {
								const setIds = Array.isArray(ids) ? ids : [];
								fileBrowserRef.current?.setFileSelection?.(new Set(setIds));
								setSelectedFileIds(new Set(setIds));
							},
							clearSelection: () => {
								fileBrowserRef.current?.setFileSelection?.(new Set());
								setSelectedFileIds(new Set());
							},
						});
					}
				} catch (e) {
					console.error('Injected action handler error:', e);
				}
				return; // Skip default switch for matched injected action
			}
		}
		switch (data.id) {
		case 'toggle_select_all': {
			if (!addSelectAllToggleAction) break;
			const selectableFiles = filesSortedByNames.filter((f) => {
				return !isSystemFolder(f);
			});
			const allVisibleSelected = selectableFiles.length > 0 && selectableFiles.every((f) => {
				return selectedFileIds.has(f.id);
			});
			if (allVisibleSelected) {
				fileBrowserRef.current.setFileSelection(new Set());
				setSelectedFileIds(new Set());
			} else {
				const allIds = selectableFiles.map((f) => {
					return f.id;
				});
				const selection = new Set(allIds);
				fileBrowserRef.current.setFileSelection(selection);
				setSelectedFileIds(selection);
			}
			break;
		}
		case ChonkyActions.ChangeSelection.id: {
			if (data.state && data.state.selectedFilesForAction) {
				const selection = new Set(data.state.selectedFilesForAction.map(f => f.id));
				setSelectedFileIds(selection);
			} else if (data.state && data.state.selectedFiles) {
				const selection = new Set(data.state.selectedFiles.map(f => f.id));
				setSelectedFileIds(selection);
			} else if (data.payload && data.payload.selection) {
				setSelectedFileIds(data.payload.selection);
			}
			break;
		}
		case ChonkyActions.EnableListView.id:
			setIsListView(true);
			break;
		case ChonkyActions.EnableGridView.id:
			setIsListView(false);
			break;
		case ChonkyActions.DeleteFiles.id: {
			const confirmed = window.confirm(
				'Are you sure you want to delete the selected items? This action cannot be undone.'
			);

			if (!confirmed) return;

			let selectFrom = selectedFileIds;
			if (data?.payload?.selectedFileIds) {
				selectFrom = data.payload.selectedFileIds;
			}

			const selectedFiles = hasSearchTerm ?
				files.filter((file) => {
					return selectFrom.has(file.id);
				}) : Array.from(selectFrom)
					.map((id) => {
						return fileMapRef.current[id];
					})
					.filter(Boolean);

			const filesToDelete = selectedFiles.filter((file) => {
				return file.id.startsWith('file_');
			});
			const foldersToDelete = selectedFiles.filter((file) => {
				return file.id.startsWith('node_') && !isSystemFolder(file);
			});

			const fileIds = filesToDelete.map((file) => {
				return file.id.replace('file_', '');
			});
			const folderIds = foldersToDelete.map((folder) => {
				return folder.id.replace('node_', '');
			});

			if (fileIds.length + folderIds.length === 0) return;

			const session = getSession();
			const headers = {
				headers: {
					'X-Auth-Session':      session.session,
					'X-Spark-API-version': commitHash,
				},
			};

			const deletePromises = [
				...fileIds.map((id) => {
					return axios.delete(`${broncoURL}/api/file/${id}?account_uuid=${accountUUID}`, headers);
				}),
				...folderIds.map((id) => {
					return axios.delete(`${broncoURL}/api/folder/${id}?account_uuid=${accountUUID}`, headers);
				}),
			];

			app.setState({
				loading: true,
			});

			Promise.all(deletePromises)
				.then(() => {
					return refreshFileBrowserData();
				})
				.catch((error) => {
					console.error('Delete error:', {
						error:    error.message,
						response: error.response?.data,
					});
					const message = error.response?.data?.message || error.response?.data || error.message;
					alert(`Deletion Failed. ${message}`);
				})
				.finally(() => {
					app.setState({
						loading: false,
					});
					setSearchQuery('');
				});
			break;
		}
		case ChonkyActions.OpenFiles.id: {
			const {
				targetFile,
				files,
			} = data.payload;
			const fileToOpen = targetFile ?? files[0];
			if (fileToOpen && FileHelper.isDirectory(fileToOpen)) {
				setSearchQuery('');
				setCurrentFolderId(fileToOpen.id);
			}
			break;
		}
		case ChonkyActions.EndDragNDrop.id: {
			handledInternalDropRef.current = true;
			const {
				draggedFile,
				destination,
				selectedFiles,
			} = data.payload;

			const filesToMove = selectedFiles.length > 0 ? selectedFiles : [ draggedFile, ];
			if (!filesToMove.length || !destination || !destination.isDir) {
				alert('Invalid move operation');
				return;
			}

			try {
				const allAlreadyInDestination = filesToMove.every((f) => {
					return f?.parentId === destination.id;
				});
				if (allAlreadyInDestination) {
					return;
				}
			} catch (e) {
				console.error('Error checking move operation:', e);
				return;
			}

			const selectedMoveItems = Array.from(selectedFileIds || [])
				.map((id) => {
					return fileMapRef.current[id];
				})
				.filter(Boolean);
			const moveItems = selectedMoveItems.length > 0 ? selectedMoveItems : filesToMove;

			const sourceFileIds = moveItems.filter((file) => !file.isDir).map((file) => {
				return file.id.startsWith('file_') ? file.id.slice(5) : file.id;
			});
			const sourceFolderIds = moveItems.filter((file) => file.isDir).map((file) => {
				return file.id.startsWith('node_') ? file.id.slice(5) : file.id;
			});
			const destFolderId = destination.id.startsWith('node_') ?
				destination.id.slice(5) :
				destination.id;

			const session = getSession();

			app.setState({
				loading: true,
			});

			const promises = [];
			const headers = {
				'X-Auth-Session':      session.session,
				'X-Spark-API-version': commitHash,
			};

			if (sourceFileIds.length > 0) {
				promises.push(axios.post(`${broncoURL}/api/file/move?account_uuid=${accountUUID}`, {
					source_file_ids: sourceFileIds,
					dest_folder_id:  destFolderId,
				}, { headers }));
			}
			
			if (sourceFolderIds.length > 0) {
				promises.push(axios.post(`${broncoURL}/api/folder/move?account_uuid=${accountUUID}`, {
					source_folder_ids: sourceFolderIds,
					dest_folder_id:  destFolderId,
				}, { headers }));
			}

			Promise.all(promises)
				.then((responses) => {
					const failed = responses.find(r => r.status !== 200);
					if (!failed) {
						refreshFileBrowserData();
					} else {
						throw new Error('Failed to move items');
					}
				})
				.catch((error) => {
					console.error('Move error:', error);
					// The move endpoint returns the block reason as a raw string body
					// (e.g. canDeleteFile's "File is still assigned to a … in this
					// account."), so fall back to that string before error.message.
					const message = Object.values(error.response?.data?.failed_files || {})[0]
						|| (typeof error.response?.data === 'string' ? error.response.data : null)
						|| error.message;

					const errorMsg = error.response?.data || '';
					let alertMsg = null;

					switch (true) {
					case typeof errorMsg === 'string' && errorMsg.includes('already exists in destination folder'):
						alertMsg = 'Move failed: Item already exists in the destination folder';
						break;
					case typeof errorMsg === 'string' && errorMsg.includes('is assigned to a system or group in this account'):
						alertMsg = 'Move failed: Item is assigned to a system or group in this account';
						break;
					case typeof errorMsg === 'string' && errorMsg.includes('cannot move a folder into itself'):
						alertMsg = 'Move failed: Cannot move a folder into itself or its own subfolders';
						break;
					default:
						alertMsg = `Move failed: ${message}`;
					}
					alert(alertMsg);
				})
				.finally(() => {
					app.setState({
						loading: false,
					});
				});
			break;
		}
		case 'custom_folder_upload': {
			openCreateFolderModal();
			break;
		}
		case 'custom_edit': {
			openEditModal();
			break;
		}
		case 'custom_file_upload': {
			openCreateFileModal();
			break;
		}
		case 'custom_file_download': {
			handleDownloadFile();
			break;
		}
		default:
			break;
		}
	}, [ isInjected, injectedActions, currentFolderId, filesSortedByNames, app, onInjectedFileOpen, addSelectAllToggleAction, selectedFileIds, hasSearchTerm, files, accountUUID, refreshFileBrowserData, openCreateFolderModal, openEditModal, openCreateFileModal, ]);

	const handleCrumbDragOver = (e, folder) => {
		if (!draggedFileRef.current || !folder?.isDir || folder.id === 'root') return;
		e.preventDefault();
		e.dataTransfer.dropEffect = 'move';
		setBreadcrumbDropTargetId(folder.id);
	};

	const handleCrumbDragLeave = (e, folder) => {
		if (e.currentTarget.contains(e.relatedTarget)) return;
		setBreadcrumbDropTargetId((prev) => {
			return prev === folder.id ? null : prev;
		});
	};

	const handleCrumbDrop = (e, folder) => {
		e.preventDefault();
		setBreadcrumbDropTargetId(null);
		setDraggingFileId(null);
		if (!draggedFileRef.current || !folder?.isDir || folder.id === 'root') return;
		const selection = new Set(fileBrowserRef.current?.getFileSelection?.() || []);
		const selectionIds = Array.from(selection);
		const draggingIsSelected = draggedFileRef.current && selection.has(draggedFileRef.current.id);
		let selectedFiles = [ draggedFileRef.current, ];
		if (draggingIsSelected && selectionIds.length > 1) {
			selectedFiles = selectionIds
				.map((id) => {
					return fileMapRef.current[id] || files.find((f) => {
						return f.id === id;
					});
				})
				.filter((f) => {
					return f && !f.isDir;
				});
			if (selectedFiles.length === 0) selectedFiles = [ draggedFileRef.current, ];
		}

		handleFileAction({
			id:      ChonkyActions.EndDragNDrop.id,
			payload: {
				draggedFile: draggedFileRef.current,
				destination: folder,
				selectedFiles,
			},
		});
		draggedFileRef.current = null;
		stopAutoScroll();
	};

	const handleCrumbClick = (e, folder, isLast) => {
		if (!isLast) {
			if (typeof onBreadcrumbClick === 'function') {
				onBreadcrumbClick(folder);
			} else {
				setSearchQuery('');
				setCurrentFolderId(folder.id);
			}
		}
	};

	const CustomDeleteAction = useMemo(() => {
		return defineFileAction({
			id:     ChonkyActions.DeleteFiles.id,
			button: {
				name:        'Delete',
				toolbar:     true,
				contextMenu: true,
				icon:        ChonkyIconName.trash,
			},
		});
	}, []);

	const CustomFolderUploadAction = useMemo(() => {
		return defineFileAction({
			id:     'custom_folder_upload',
			button: {
				name:        'New Folder',
				toolbar:     true,
				contextMenu: true,
				icon:        ChonkyIconName.folderCreate,
			},
		});
	}, []);

	const CustomFileDownload = useMemo(() => {
		return defineFileAction({
			id:     'custom_file_download',
			button: {
				name:        'Download File(s)',
				toolbar:     true,
				contextMenu: true,
				icon:        ChonkyIconName.download,
			},
		});
	}, []);

	const CustomFileUploadAction = useMemo(() => {
		return defineFileAction({
			id:     'custom_file_upload',
			button: {
				name:        'Upload File(s)',
				toolbar:     true,
				contextMenu: true,
				icon:        ChonkyIconName.file,
			},
		});
	}, []);

	const dynamicInjectedFileActions = useMemo(() => {
		if (!isInjected) return [];
		if (Array.isArray(injectedActions) && injectedActions.length > 0) {
			return injectedActions
				.filter((a) => {
					return a && a.id;
				}) // ensure valid shape
				.map((a) => {
					return defineFileAction({
						id:     a.id,
						button: {
							name:        a.name || a.id,
							toolbar:     true,
							contextMenu: true,
							icon:        a.icon || ChonkyIconName.folderCreate,
						},
					});
				});
		}
		return [];
	}, [ isInjected, injectedActions, ]);

	// const CustomEditAction = defineFileAction({
	// 	id: 'custom_edit',
	// 	button: {
	// 		name: 'Edit',
	// 		toolbar: true,
	// 		contextMenu: true,
	// 		icon: ChonkyIconName.config
	// 	},
	// });

	const searchResultsLenght = (searchResults?.folders || []).length + (searchResults?.files || []).length;

	// Select/Deselect All toggle (only when addSelectAllToggleAction true)
	const SelectAllToggleAction = useMemo(() => {
		if (!addSelectAllToggleAction) return null;
		const allVisibleSelected = filesSortedByNames.length > 0 && filesSortedByNames.every((f) => {
			selectedFileIds.has(f.id);
		});
		return defineFileAction({
			id:     'toggle_select_all',
			button: {
				name:        allVisibleSelected ? 'Deselect All' : 'Select All',
				toolbar:     true,
				contextMenu: false,
				icon:        allVisibleSelected ? ChonkyIconName.clearSelection : ChonkyIconName.selectAllFiles,
			},
		});
	}, [ addSelectAllToggleAction, filesSortedByNames, selectedFileIds, ]);

	// Show/Hide menu state
	const [ showColumnMenu, setShowColumnMenu ] = useState(false);
	const columnMenuRef = useRef(null);
	const [ columnMenuStyle, setColumnMenuStyle ] = useState({
		position: 'absolute',
		top: 0,
		left: 0,
	});
 
	useEffect(() => {
		const onDocClick = (e) => {
			if (showColumnMenu && columnMenuRef.current && !columnMenuRef.current.contains(e.target)) {
				setShowColumnMenu(false);
			}
		};
		document.addEventListener('click', onDocClick);
		return () => {
			return document.removeEventListener('click', onDocClick);
		};
	}, [ showColumnMenu ]);
 
	useEffect(() => {
		if (!showColumnMenu) return;
		const selectors = ['.chonky-toolbar', '.chonky-toolbar__container', '.chonky-fileToolbar', '.chonky-toolbarContainer'];
		let toolbar = null;
		for (const s of selectors) {
			toolbar = document.querySelector(s);
			if (toolbar) break;
		}

		let top = 0;
		let left = 0;
		const menuWidth = 240;

		let targetBtn = null;
		if (toolbar) {
			try {
				const buttons = toolbar.querySelectorAll('button, [role="button"]');
				for (const b of buttons) {
					const txt = (b.textContent || '').trim();
					const aria = (b.getAttribute && (b.getAttribute('aria-label') || b.title || '') ) || '';
					if (txt.includes('Show/Hide') || aria.includes('Show/Hide')) {
						targetBtn = b;
						break;
					}
				}
			} catch (e) { /* ignore DOM read errors */ }
		}

		const targetRect = (targetBtn || toolbar)?.getBoundingClientRect?.();
		const scrollRect = scrollContainerRef.current?.getBoundingClientRect?.();

		const desiredTop = targetRect ? (targetRect.bottom + window.scrollY + 6) : (wrapperRef.current ? (wrapperRef.current.getBoundingClientRect().top + window.scrollY + 40) : window.scrollY + 40);
		const desiredLeft = targetRect ? (window.scrollX + targetRect.left) : (wrapperRef.current ? (wrapperRef.current.getBoundingClientRect().left + window.scrollX + 20) : window.scrollX + 8);

		if (scrollRect) {
			const minTop = scrollRect.top + window.scrollY + 6;
			const maxTop = scrollRect.bottom + window.scrollY - 40;
			top = Math.min(Math.max(desiredTop, minTop), maxTop);

			const minLeft = scrollRect.left + window.scrollX + 8;
			const maxLeft = scrollRect.right + window.scrollX - menuWidth - 8;
			left = Math.min(Math.max(desiredLeft, minLeft), Math.max(maxLeft, minLeft));
		} else if (targetRect || toolbar) {
			const rect = (targetRect || toolbar.getBoundingClientRect());
			top = rect.bottom + window.scrollY + 6;
			const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
			left = Math.min(Math.max(desiredLeft, window.scrollX + 8), window.scrollX + viewportWidth - menuWidth - 8);
		} else if (wrapperRef.current) {
			const rect = wrapperRef.current.getBoundingClientRect();
			top = rect.top + window.scrollY + 40;
			left = rect.left + window.scrollX + 20;
		}

		setColumnMenuStyle({
			position: 'absolute',
			top: `${Math.round(top)}px`,
			left: `${Math.round(left)}px`,
			width: `${menuWidth}px`,
			maxHeight: scrollRect ? `${Math.max(300, Math.floor(scrollRect.height - 16))}px` : undefined,
			overflowY: scrollRect ? 'auto' : undefined,
		});
	}, [ showColumnMenu, wrapperRef, filesSortedByNames, scrollContainerRef ]);
 
	const ShowHideColumnsAction = useMemo(() => {
		return defineFileAction({
			id:     'toggle_columns',
			button: {
				name:        'Show/Hide',
				toolbar:     true,
				contextMenu: false,
				icon:        [ 'fas', 'check-circle' ],
			},
		});
	}, []);

	const ChonkyToolbarActions = useMemo(() => {
		return [
			addSelectAllToggleAction && !readOnly && SelectAllToggleAction,
			!isRoot && !readOnly && !hasSearchTerm && hasWriteAccess && CustomFileUploadAction,
			!isRoot && !readOnly && !isInjected && CustomFileDownload,
			!isRoot && !readOnly && hasDeleteAccess && CustomDeleteAction,
			!isRoot && !readOnly && !hasSearchTerm && hasWriteAccess && CustomFolderUploadAction,
			isListView && ShowHideColumnsAction,
			...dynamicInjectedFileActions,
			!hideListViewOptions && ChonkyActions.EnableListView,
			!hideListViewOptions && ChonkyActions.EnableGridView,
		].filter(Boolean);
	}, [
		addSelectAllToggleAction,
		SelectAllToggleAction,
		isRoot,
		readOnly,
		hasSearchTerm,
		hasWriteAccess,
		hasDeleteAccess,
		CustomFileUploadAction,
		isInjected,
		CustomFileDownload,
		CustomDeleteAction,
		CustomFolderUploadAction,
		dynamicInjectedFileActions,
		hideListViewOptions,
		isListView,
		ShowHideColumnsAction,
	]);

	const searchComponent = useMemo(() => {
		return (
			<div className={style.searchContainer} data-test-id="searchContainer">
				<div className={style.searchInnerContainer} data-test-id="searchInnerContainer">
					<button
						onClick={() => {
							setSearchQuery('');
						}}
						className={`button ${(currentFolderId === 'root' && !isInjected) ? style.disabledButton : ''}`}
						aria-label="Clear search"
						data-test-id="clearSearchButton"
					>
						<CustomIcon icon='times' />
					</button>
					<input
						type="text"
						placeholder={isInjected ? 'Search items...' : 'Search all files and folders - minimum 3 characters'}
						value={searchQuery}
						onChange={(e) => {
							setSearchQuery(e.target.value);
						}}
						className={style.searchInput}
						data-test-id="searchInput"
						disabled={!isInjected && currentFolderId === 'root'}
					/>
				</div>
				{injectedHeaderDropdown && (isInjected || injectedHeaderDropdownComponent) && (
					<div className={style.dropdownContainerWrapper}>
						{injectedHeaderDropdownComponent ? (
							injectedHeaderDropdownComponent({
								options: injectedHeaderDropdown,
								initialValue: injectedHeaderDropdownInitialValue,
								onChange: (selectedOptions) => {
									if (onInjectedHeaderDropdownChange) {
										onInjectedHeaderDropdownChange(selectedOptions);
									}
								},
							})
						) : (
							<ContentManagerDropdown
								options={injectedHeaderDropdown}
								initialValue={injectedHeaderDropdownInitialValue}
								onChange={(selectedOptions) => {
									if (onInjectedHeaderDropdownChange) {
										onInjectedHeaderDropdownChange(selectedOptions);
									}
								}}
								label="Content Managers"
							/>
						)}
					</div>
				)}
				{!isInjected && hasSearchTerm && searchResultsLenght > 0 && (
					<div className={style.searchStatus}>
						Found {searchResultsLenght} result{searchResultsLenght !== 1 ? 's' : ''}
					</div>
				)}
			</div>
		);
	}, [ currentFolderId, isInjected, searchQuery, injectedHeaderDropdown, injectedHeaderDropdownInitialValue, injectedHeaderDropdownComponent, hasSearchTerm, searchResultsLenght, onInjectedHeaderDropdownChange, ]);

	const originalHandleFileAction = handleFileAction;
	const wrappedHandleFileAction = useCallback((data) => {
		if (data.id === 'toggle_columns') {
			setShowColumnMenu((v) => {
				return !v;
			});
			return;
		}
		originalHandleFileAction(data);
	}, [ originalHandleFileAction ]);

	// const storagePrefix = `chonky_${accountUUID || 'global'}`;

	const loadColumnState = (key, defaultVal) => {
		try {
			const raw = localStorage.getItem(`${storagePrefix}_${key}`);
			return raw ? JSON.parse(raw) : defaultVal;
		} catch (e) { return defaultVal; }
	};

	const DEFAULT_COLUMN_WIDTHS = {
		order: '72px',
		name: '240px',
		description: '320px',
		size: '96px',
		resolution: '96px',
		path: '240px',
	};

	const computeInitialColumnWidths = () => {
		if (isInjected && injectedFileMap && Object.keys(injectedColumnDefaults || {}).length > 0) {
			return { ...DEFAULT_COLUMN_WIDTHS, ...injectedColumnDefaults };
		}
		return DEFAULT_COLUMN_WIDTHS;
	};

	const MIN_COLUMN_WIDTHS_PX = {
		order: 72,
		name: 120,
		description: 120,
		size: 64,
		resolution: 64,
		path: 120,
	};

	const [ columnWidths, setColumnWidths, ] = useState(() => {
		return loadColumnState('widths', computeInitialColumnWidths());
	});
	const [ columnVisibility, setColumnVisibility, ] = useState(() => {
		return loadColumnState('visibility', {
			order: true, name: true, description: true, size: true, resolution: true, path: true,
		});
	});

	useEffect(() => {
		try { localStorage.setItem(`${storagePrefix}_widths`, JSON.stringify(columnWidths)); } catch (e) {}
	}, [ columnWidths, storagePrefix ]);

	useEffect(() => {
		try { localStorage.setItem(`${storagePrefix}_visibility`, JSON.stringify(columnVisibility)); } catch (e) {}
	}, [ columnVisibility, storagePrefix ]);

	const setColumnWidth = useCallback((colId, width) => {
		setColumnWidths((prev) => {
			const normalize = (w) => {
				if (typeof w === 'number') {
					const min = MIN_COLUMN_WIDTHS_PX[colId] || 40;
					const px = Math.max(Math.round(w), min);
					return `${px}px`;
				}
				if (typeof w === 'string') {
					const pxMatch = w.match(/^(\d+)(px)?$/);
					if (pxMatch) {
						const min = MIN_COLUMN_WIDTHS_PX[colId] || 40;
						const px = Math.max(parseInt(pxMatch[1], 10), min);
						return `${px}px`;
					}
					if (w === 'auto' || w.trim() === '') {
						return prev[colId] || DEFAULT_COLUMN_WIDTHS[colId] || `${MIN_COLUMN_WIDTHS_PX[colId] || 120}px`;
					}
					return w;
				}
				return prev[colId] || DEFAULT_COLUMN_WIDTHS[colId];
			};

			const next = { ...prev, [colId]: normalize(width) };
			try { localStorage.setItem(`${storagePrefix}_widths`, JSON.stringify(next)); } catch (e) {}
			return next;
		});
	}, [ storagePrefix ]);

	const toggleColumnVisibility = useCallback((colId) => {
		setColumnVisibility((prev) => {
			const next = { ...prev, [colId]: !prev[colId] };
			try { localStorage.setItem(`${storagePrefix}_visibility`, JSON.stringify(next)); } catch (e) {}
			return next;
		});
	}, [ storagePrefix ]);

	const columnsForMenu = useMemo(() => {
			const capitalize = (s) => {
				return String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => {
					return c.toUpperCase();
				});
			};
			const excludedCols = new Set(['selection', 'actions', 'id', 'isDir', 'childrenIds', 'parentId', 'thumbnailUrl', 'modDate', 'canDrag', 'canDrop', 'isOpenable']);

			const defaultOrder = ['order','name','description','size','resolution','path'];
			const defaultLabels = {
				order: 'Order',
				name: 'Name',
				description: 'Descriptive Name',
				size: 'Size',
				resolution: 'Resolution',
				path: 'Path',
			};

			if (isInjected && Array.isArray(resolvedInjectedColumns) && resolvedInjectedColumns.length > 0) {
				return resolvedInjectedColumns
					.filter((c) => {
						return c && c.id && !excludedCols.has(String(c.id).toLowerCase());
					})
					.map((c) => {
						return {
							id: String(c.id),
							label: c.label || c.name || defaultLabels[c.id] || capitalize(c.id),
						};
					});
			}

			return defaultOrder
				.filter((id) => {
					return !excludedCols.has(id);
				})
				.map((id) => {
					return {
						id, label: defaultLabels[id] || capitalize(id),
					};
				});
		}, [ isInjected, resolvedInjectedColumns, ]);
	
		useEffect(() => {
			if (!Array.isArray(columnsForMenu) || columnsForMenu.length === 0) return;
			setColumnVisibility((prev) => {
				const next = { ...(prev || {}) };
				let changed = false;
				for (const col of columnsForMenu) {
					const id = String(col.id);
					if (!(id in next)) {
						next[id] = true;
						changed = true;
					}
				}
				if (changed) {
					try { localStorage.setItem(`${storagePrefix}_visibility`, JSON.stringify(next)); } catch (e) {}
					return next;
				}
				return prev;
			});
		}, [ columnsForMenu, storagePrefix, setColumnVisibility ]);
	return (
		<>
			<div
			    ref={wrapperRef}
				tabIndex={-1}
				className={`${style.fileBrowserWrapper} chonkyFont`}
				style={{ position: 'relative' }}
				onDragEnter={handleWrapperDragEnter}
				onDragLeave={handleWrapperDragLeave}
				onDragOver={handleWrapperDragOver}
				onDragEnd={stopAutoScroll}
				onDrop={handleWrapperDrop}
			>
				{isWrapperDragging && (
					<div className={style.dropZoneOverlay}>
						<div className={style.dropZoneText}>Drop files to upload</div>
					</div>
				)}
				{searchComponent}
				<FileBrowser
					ref={fileBrowserRef}
					files={filesSortedByNames}
					folderChain={folderChain}
					onFileAction={wrappedHandleFileAction}
					fileActions={ChonkyToolbarActions}
					disableDefaultFileActions={true}
					disableDragAndDropProvider={true}
					defaultFileViewActionId={isListView ? ChonkyActions.EnableListView.id : ChonkyActions.EnableGridView.id}
				>
					<CustomNavbar
						folderChain={folderChain}
						handleCrumbDragOver={handleCrumbDragOver}
						handleCrumbDragLeave={handleCrumbDragLeave}
						handleCrumbDrop={handleCrumbDrop}
						handleCrumbClick={handleCrumbClick}
						breadcrumbDropTargetId={breadcrumbDropTargetId}
					/>
					<FileToolbar />
					{showColumnMenu && isListView && (() => {
						const menuElement = (
							<div ref={columnMenuRef} className={`${style.columnMenu} ${style.columnMenuFloating}`} style={columnMenuStyle}>
								<div style={{ fontWeight: 600, marginBottom: 6 }}>Columns</div>
								{columnsForMenu.map((col) => {
									return (
										<label key={col.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
											<input
												type="checkbox"
												checked={!!columnVisibility[col.id]}
												onChange={() => {
													return toggleColumnVisibility(col.id);
												}}
											/>
											<span style={{ textTransform: 'capitalize' }}>{col.label}</span>
										</label>
									);
								})}
							</div>
						);

						if (scrollContainerRef.current) {
							try {
								return createPortal(menuElement, scrollContainerRef.current);
							} catch (e) {
								return menuElement;
							}
						}
						return menuElement;
					})()}
					<div className={style.customFileListContainer} ref={scrollContainerRef}>
						<CustomFileList
							app={app}
							accountUUID={accountUUID}
							isListView={isListView}
							isInjected={isInjected}
							hideActionsColumn={hideActionsColumn}
							resolvedInjectedColumns={resolvedInjectedColumns}
							readOnly={readOnly}

							files={filesSortedByNames}
							selectedFileIds={selectedFileIds}
							handleCheckboxChange={handleCheckboxChange}
							handleDragStart={handleDragStart}
							handleDragEnd={handleDragEnd}
							handleDragOver={handleDragOver}
							handleDragLeave={handleDragLeave}
							handleDrop={handleDrop}
							handleSort={handleSort}
							handleDelete={handleDelete}
							renderSortArrow={renderSortArrow}
							renderFileIcon={renderFileIcon}
							dropTarget={dropTarget}
							dragOverIndex={dragOverIndex}
							onInjectedReorder={onInjectedReorder}
							onInjectedReorderProgress={onInjectedReorderProgress}
							setCurrentFolderId={setCurrentFolderId}
							onInjectedFileOpen={onInjectedFileOpen}

							fileBrowserRef={fileBrowserRef}
							setSelectedFileIds={setSelectedFileIds}
							mode={mode}
							onSelectImage={onSelectImage}
							onSelectFolder={onSelectFolder}
							allowedFormats={allowedFormats}
							setSearchQuery={setSearchQuery}
							hideEdit={hideEdit}
							hideEditAndDeleteButtons={hideEditAndDeleteButtons}
							onInjectedEdit={onInjectedEdit}
							onInjectedDelete={onInjectedDelete}
							onInjectedCalendar={onInjectedCalendar}
							draggingFileId={draggingFileId}
							getDisplayPath={getDisplayPath}
							isRoot={isRoot}
							hasWriteAccess={hasWriteAccess}
							hasDeleteAccess={hasDeleteAccess}
							openEditModal={openEditModal}
							handleDownloadFile={handleDownloadFile}
							handleFileAction={handleFileAction}
							columnWidths={columnWidths}
							setColumnWidth={setColumnWidth}
							columnVisibility={columnVisibility}
							toggleColumnVisibility={toggleColumnVisibility}
						/>
					</div>
				</FileBrowser>
			</div>
		</>
	);
}

export default ChonkyFileBrowser;